import React from 'react';
import { Box, Typography, Link, Avatar } from '@mui/material';
import { HeadcounterEntry } from '../../Types/Incident';
import { appColors } from '../../Config/Theme';

interface HeadcounterActivityPanelProps {
  headcounters: HeadcounterEntry[];
  onViewAll?: () => void;
}

const HeadcounterActivityPanel: React.FC<HeadcounterActivityPanelProps> = ({ headcounters, onViewAll }) => {
  const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).map((w) => w.charAt(0)).slice(0, 2).join('').toUpperCase();

  const colors = appColors.avatarColors;

  return (
    <Box
      sx={{
        bgcolor: appColors.surface.card,
        borderRadius: '12px',
        border: `1px solid ${appColors.border.default}`,
        p: 2.5,
        flex: '1 1 0',
        minWidth: { xs: 0, sm: 240 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Headcounter Activity
        </Typography>
        {onViewAll && (
          <Link component="button" variant="caption" onClick={onViewAll} sx={{ fontWeight: 600, color: appColors.primary.main }}>
            View All
          </Link>
        )}
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ flex: '2 1 0', color: appColors.text.placeholder, fontWeight: 600 }}>Headcounter</Typography>
        <Typography variant="caption" sx={{ flex: '1 1 0', textAlign: 'center', color: appColors.text.placeholder, fontWeight: 600 }}>Last Update</Typography>
        <Typography variant="caption" sx={{ flex: '1 1 0', textAlign: 'right', color: appColors.text.placeholder, fontWeight: 600 }}>Updates</Typography>
      </Box>

      {headcounters.length === 0 && (
        <Typography variant="body2" sx={{ color: appColors.text.disabled, py: 2, textAlign: 'center' }}>
          No headcount updates have been submitted yet.
        </Typography>
      )}

      {headcounters.slice(0, 5).map((h, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1, borderTop: `1px solid ${appColors.border.light}` }}>
          <Box sx={{ flex: '2 1 0', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: colors[idx % colors.length] }}>
              {initials(h.name)}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.2 }}>{h.name}</Typography>
              <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>{h.viewName}</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ flex: '1 1 0', textAlign: 'center', color: appColors.text.caption }}>{h.lastUpdate}</Typography>
          <Typography variant="body2" sx={{ flex: '1 1 0', textAlign: 'right', fontWeight: 700, color: appColors.text.heading }}>{h.updates}</Typography>
        </Box>
      ))}

      {onViewAll && headcounters.length > 0 && (
        <Link component="button" variant="caption" onClick={onViewAll} sx={{ mt: 1, display: 'block', color: appColors.primary.main, fontWeight: 600 }}>
          View all headcounters →
        </Link>
      )}
    </Box>
  );
};

export default HeadcounterActivityPanel;
